import { getDomainFaviconUrl } from '@heidi/utils';
import type {
  EvidenceSourceClickedEventProps,
  HtmlEmbedHint,
  Source,
} from '../../contracts';
import {
  PARTNER_PROXY_DOMAINS,
  buildViewerSource,
  getDomainFromUrl,
  inferPartnerKeyFromProxyUrl,
  isMimsPartnerSource,
  resolveMimsHtmlEmbed,
} from '../../contracts';
import { shouldOpenSourceInViewer } from './sourceViewerRouting';

export type SearchSourceOpenInteraction =
  | 'open_library_document'
  | 'open_in_viewer'
  | 'open_in_new_tab';

export type SearchBlockSurfaceBlock =
  | 'collapsed_sources_summary'
  | 'inline_search_block'
  | 'search_block';

export interface OpenSearchBlockSourceParams {
  source: Source;
  isMobile: boolean;
  /** Port 7, threaded through to `shouldOpenSourceInViewer`. */
  isAuthenticatedUrl: (url: string, embed?: HtmlEmbedHint) => boolean;
  citationNumber: number;
  surfaceBlock: SearchBlockSurfaceBlock;
  /** Set for inline search blocks; omitted from analytics on collapsed rollup. */
  searchBlockQuery?: string;
  chatId?: string;
  messageId?: string;
  sessionId?: string | null;
}

export interface OpenSearchBlockSourceActions {
  trackSourceClick: (props: EvidenceSourceClickedEventProps) => void;
  openLibraryDocument: (documentId: string) => void;
  openInViewer: (source: Source) => void;
  openInNewTab: (url: string) => void;
}

export function isPartnerSearchSource(source: Source): boolean {
  return (
    source.type === 'integration_partner' || source.source_type === 'integration_partner'
  );
}

/** MIMS monographs use Heidi HTML proxy paths; BE may omit `embed` on sources[] rows. */
export function isMimsSearchSource(source: Source): boolean {
  return isMimsPartnerSource(source.partner_type, source.url);
}

/** Align search-block sources with inline citation payloads for viewer routing. */
export function enrichSearchSourceForViewer(source: Source): Source {
  const embed = resolveMimsHtmlEmbed(source.embed, source.partner_type, source.url);
  if (!embed || embed === source.embed) return source;
  return { ...source, embed };
}

/**
 * Subtitle shown beside a search-block source title. Prefers backend-provided
 * logical domain / partner display name so opaque API proxy paths (e.g. MIMS
 * `/api/v2/ml-scribe/.../html`) don't render as the misleading label "api".
 */
export function getSearchSourceSubtitle(source: Source): string {
  if (source.domain) return source.domain;
  if (source.display_name && isPartnerSearchSource(source)) {
    return source.display_name;
  }
  const partnerKey =
    source.partner_type ?? inferPartnerKeyFromProxyUrl(source.url ?? '') ?? undefined;
  if (partnerKey) {
    const logicalDomain = PARTNER_PROXY_DOMAINS[partnerKey];
    if (logicalDomain) return logicalDomain;
  }
  return source.url ? getDomainFromUrl(source.url) : '';
}

export function resolveSearchSourceOpenInteraction(
  source: Source,
  isMobile: boolean,
  isAuthenticatedUrl: (url: string, embed?: HtmlEmbedHint) => boolean
): SearchSourceOpenInteraction {
  const normalized = enrichSearchSourceForViewer(source);

  if (
    (normalized.type === 'user_document' || normalized.source_type === 'user_document') &&
    normalized.document_id
  ) {
    return 'open_library_document';
  }
  if (
    normalized.url &&
    shouldOpenSourceInViewer({
      url: normalized.url,
      embed: normalized.embed,
      isMobile,
      isAuthenticatedUrl,
    })
  ) {
    return 'open_in_viewer';
  }
  return 'open_in_new_tab';
}

/** Analytics domain — same precedence as subtitle, falls back to URL hostname. */
export function getSearchSourceAnalyticsDomain(source: Source): string {
  return getSearchSourceSubtitle(source);
}

/** Build viewer state from a search-block `Source` (matches citation badge wiring). */
export function buildViewerSourceFromSearchSource(source: Source) {
  const normalized = enrichSearchSourceForViewer(source);
  return buildViewerSource(
    {
      url: normalized.url,
      title: normalized.title || '',
      domain: normalized.domain,
      source_id: normalized.source_id,
      display_name: normalized.display_name,
      embed: normalized.embed,
    },
    getDomainFaviconUrl
  );
}

/** Shared click routing for CollapsedSourcesSummary and InlineSearchBlock rows. */
export function openSearchBlockSource(
  params: OpenSearchBlockSourceParams,
  actions: OpenSearchBlockSourceActions
): void {
  const {
    source,
    isMobile,
    isAuthenticatedUrl,
    citationNumber,
    surfaceBlock,
    searchBlockQuery,
    chatId,
    messageId,
    sessionId,
  } = params;

  const interaction = resolveSearchSourceOpenInteraction(
    source,
    isMobile,
    isAuthenticatedUrl
  );
  const libraryDocumentId =
    source.type === 'user_document' || source.source_type === 'user_document'
      ? source.document_id
      : undefined;

  const baseProps: EvidenceSourceClickedEventProps = {
    source_url: source.url || '',
    source_domain: getSearchSourceAnalyticsDomain(source),
    source_title: source.title || undefined,
    source_type: source.source_type ?? source.type,
    collections: source.collections?.length ? source.collections : undefined,
    surface: 'search_block',
    surface_block: surfaceBlock,
    citation_number: citationNumber,
    chat_id: chatId,
    message_id: messageId,
    session_id: sessionId ?? undefined,
    ...(searchBlockQuery !== undefined
      ? { search_block_query: searchBlockQuery || undefined }
      : {}),
  };

  if (interaction === 'open_library_document' && libraryDocumentId) {
    actions.trackSourceClick({ ...baseProps, interaction: 'open_library_document' });
    actions.openLibraryDocument(libraryDocumentId);
    return;
  }

  actions.trackSourceClick({
    ...baseProps,
    interaction: interaction === 'open_in_viewer' ? 'open_in_viewer' : 'open_in_new_tab',
  });

  if (!source.url) return;

  if (interaction === 'open_in_viewer') {
    actions.openInViewer(source);
    return;
  }

  actions.openInNewTab(source.url);
}
